/** Первая доступная страница после входа / клик по логотипу. */
import { isGlobalTeamUser } from '@/i18n/localeStorage.js'

/**
 * Fallback без SoH: первый доступный реестр.
 * Submissions to MoH (реестр) сюда не входит — для SoH всегда `main` (Главная).
 */
const MAIN_ROUTE_CANDIDATES = [
  ['products_view', 'products.list'],
  ['taxes_view', 'taxes.list'],
  ['sites_view', 'site.list'],
  ['gmp_status_view', 'gmpStatusList.list'],
  ['gmp_submission_view', 'gmpForSubmission.list'],
  ['eaeu_contacts_view', 'eaeuProductContacts.list'],
  ['documents_view', 'documents.list'],
  ['rsd_view', 'rsd.list'],
  ['regspec', 'analytics.list'],
  ['reglead', 'analytics.list'],
  ['analytics_view', 'analytics.list'],
  ['remote_dossier_projects', 'remoteProjects.list'],
  ['remote_manage', 'remoteManagement.list'],
  ['users_edit', 'users.list'],
  ['catalog_view', 'templates.list'],
  ['catalog_edit', 'templates.list'],
]

function firstAccessibleRoute(userStore, candidates) {
  for (const [rbac, name] of candidates) {
    if (userStore.can(rbac)) {
      return { name }
    }
  }
  return null
}

/**
 * Пользователь с напрямую назначенной бизнес-ролью Viewer
 * (не через дочерние права sa и т.п.).
 *
 * @param {object} userStore Pinia user store.
 * @returns {boolean}
 */
export function isViewerUser(userStore) {
  const roles = userStore?.user?.roles
  if (!Array.isArray(roles)) {
    return false
  }
  return roles.some(role => role?.service === 'ltms' && role.name === 'viewer')
}

/**
 * Пользователь с напрямую назначенной бизнес-ролью Бухгалтер (buh).
 *
 * @param {object} userStore Pinia user store.
 * @returns {boolean}
 */
export function isBuhUser(userStore) {
  const roles = userStore?.user?.roles
  if (!Array.isArray(roles)) {
    return false
  }
  return roles.some(role => role?.service === 'ltms' && role.name === 'buh')
}

/**
 * Пользователь с напрямую назначенной ролью «Сотрудник медицинского отдела» (med).
 *
 * @param {object} userStore Pinia user store.
 * @returns {boolean}
 */
export function isMedUser(userStore) {
  const roles = userStore?.user?.roles
  if (!Array.isArray(roles)) {
    return false
  }
  return roles.some(role => role?.service === 'ltms' && role.name === 'med')
}

/**
 * Пользователь с напрямую назначенной ролью «Сотрудник фармаконадзора» (pv).
 *
 * @param {object} userStore Pinia user store.
 * @returns {boolean}
 */
export function isPvUser(userStore) {
  const roles = userStore?.user?.roles
  if (!Array.isArray(roles)) {
    return false
  }
  return roles.some(role => role?.service === 'ltms' && role.name === 'pv')
}

/**
 * Пользователь с напрямую назначенной ролью «Координатор» (regcoord).
 *
 * @param {object} userStore Pinia user store.
 * @returns {boolean}
 */
export function isRegcoordUser(userStore) {
  const roles = userStore?.user?.roles
  if (!Array.isArray(roles)) {
    return false
  }
  return roles.some(role => role?.service === 'ltms' && role.name === 'regcoord')
}

/**
 * Пользователь с напрямую назначенной ролью «Специалист GxP» (gxp).
 *
 * @param {object} userStore Pinia user store.
 * @returns {boolean}
 */
export function isGxpUser(userStore) {
  const roles = userStore?.user?.roles
  if (!Array.isArray(roles)) {
    return false
  }
  return roles.some(role => role?.service === 'ltms' && role.name === 'gxp')
}

/** Роли LTMS, которым доступно избранное подач (звезда). */
const SUBMISSION_FAVORITE_ROLES = new Set([
  'regspec',
  'reglead',
  'viewer',
  'med',
  'pv',
])

/**
 * Избранное подач (звезда в карточке / колонке / канбане) —
 * специалист/руководитель регистрации, Viewer, мед.отдел, ФН и администратор (sa).
 * Право submissions_view само по себе звезду не даёт.
 * Администратор включает все роли, поэтому звезда у него есть.
 *
 * @param {object} userStore Pinia user store.
 * @returns {boolean}
 */
export function canUseSubmissionFavorites(userStore) {
  if (typeof userStore?.can === 'function' && userStore.can('sa')) {
    return true
  }
  const roles = userStore?.user?.roles
  if (!Array.isArray(roles)) {
    return false
  }
  return roles.some(
    role => role?.service === 'ltms' && SUBMISSION_FAVORITE_ROLES.has(role.name)
  )
}

/**
 * Дефолтный маршрут: стартовая (home) для всех авторизованных.
 * Реестры — только как fallback при отказе в доступе к home.
 *
 * @param {object} [_userStore] Pinia user store (не используется, для совместимости вызовов).
 * @returns {object} route location.
 */
export function resolveDefaultRoute(_userStore) {
  return { name: 'home' }
}

/**
 * Куда вести логотип SOYKA со стартовой (не для глобалов).
 * При праве SoH — всегда Главная (`main` со списком подач), не реестр `/submissions`.
 * Viewer / Бухгалтер / Координатор / GxP — тоже `main`, не первый реестр.
 * Иначе — первый доступный реестр (Tax, Sites, …).
 */
export function resolveMainRoute(userStore) {
  if (isGlobalTeamUser(userStore.user)) {
    return { name: 'home' }
  }
  if (
    userStore.can('submissions_view')
    || isViewerUser(userStore)
    || isBuhUser(userStore)
    || isRegcoordUser(userStore)
    || isGxpUser(userStore)
  ) {
    return { name: 'main' }
  }
  return firstAccessibleRoute(userStore, MAIN_ROUTE_CANDIDATES) || { name: 'profile.item' }
}

/**
 * Маршрут сразу после авторизации — всегда стартовая.
 * returnUrl игнорируем: все роли (включая buh / gxp) должны попадать на home.
 *
 * @param {object} [_userStore] Pinia user store (не используется).
 * @param {*} [_returnUrl] сохранённый URL (игнорируется).
 * @returns {object} route location.
 */
export function resolvePostLoginRoute(_userStore, _returnUrl) {
  return { name: 'home' }
}
